import React, {Component} from "react"
import {orderBy} from "lodash"

import authentication from "@irati/auth"
import id from "@irati/utils/id"
import {InjectIntl, injectIntl} from "@irati/utils/lang"
import OccasionsService from "@irati/recognition/services/Occasions"
import FullModal from "@irati/ui/src/FullModal"
import {Body} from "@irati/ui/src/Typography"
import Occasion from "@irati/types"

import OccasionsTab from "./OccasionsTab"
import EditForm from "./EditForm"
import TitleAndCreate from "./TitleAndCreate"
import {Tabs} from "./styled"

interface Props extends InjectIntl {}

interface State {
  showCreateModal: boolean
  activeOccasions: Occasion[]
  hiddenOccasions: Occasion[]
  selectedOccasion: Occasion | null
  activeKey: string
}

class CompanyValuesList extends Component<Props, State> {
  state: State = {
    activeOccasions: [],
    hiddenOccasions: [],
    selectedOccasion: null,
    showCreateModal: false,
    activeKey: "active"
  }

  componentDidMount() {
    this.getOccasions()
  }

  getOccasions = () => {
    OccasionsService.getAll().then((occasions: Occasion[]) => {
      const sortedOccasions = orderBy(occasions, "createdAt", "desc")
      const activeOccasions = sortedOccasions.filter((occasion) => !occasion.archived)
      const hiddenOccasions = sortedOccasions.filter((occasion) => occasion.archived)

      this.setState({activeOccasions, hiddenOccasions})
    })
  }

  editOccasion = (selectedOccasion: Occasion) => {
    this.setState({selectedOccasion}, this.showModal)
  }

  showModal = () => {
    this.setState({showCreateModal: true})
  }

  closeModal = () => {
    this.setState({
      showCreateModal: false,
      selectedOccasion: null
    })
  }

  upsertOccasion = (name: string, tag: string, iconHref: string, occasionId?: string) => {
    const occasion: Occasion = {
      name,
      desc: name,
      tag,
      icon: {
        href: iconHref,
        alt: name
      }
    }
    if (occasionId) {
      return OccasionsService.update(occasion, occasionId).then(() => {
        this.closeModal()
        this.getOccasions()
      })
    } else {
      return OccasionsService.create(occasion).then(() => {
        this.closeModal()
        this.getOccasions()
      })
    }
  }

  hideOccasion = (occasion: Occasion) => {
    OccasionsService.hide(occasion).then(this.getOccasions)
  }

  showOccasion = async (occasion: Occasion) => {
    OccasionsService.show(occasion).then(this.getOccasions)
  }

  render() {
    const {formatMessage} = this.props
    const {activeOccasions, hiddenOccasions, showCreateModal, selectedOccasion, activeKey} = this.state
    const idNamespace = id("occasion-tabs", this.props)
    const isRecognitionEnabled = authentication.apps("recognition")

    return (
      <>
        <TitleAndCreate
          title="admin.occasions.list.header"
          buttonLabel="admin.occasions.button.create"
          showForm={this.showModal}
          isRecognitionEnabled={!!isRecognitionEnabled}
        />

        {!isRecognitionEnabled ? (
          <>
            <Body marginTop={4}>{formatMessage({id: "admin.occasions.celebrationHubDisabled.p1"})}</Body>
            <Body marginTop={2}>{formatMessage({id: "admin.occasions.celebrationHubDisabled.p2"})}</Body>
          </>
        ) : (
          <>
            <Tabs
              id="companyCelebrationTab"
              mountOnEnter
              unmountOnExit
              activeKey={activeKey}
              onSelect={(value) => {
                this.setState({activeKey: value})
              }}
              tabs={[
                {
                  title: formatMessage({id: "admin.occasions.tabs.active"}, {count: activeOccasions.length}),
                  key: "active",
                  component: (
                    <OccasionsTab
                      items={activeOccasions}
                      description="admin.occasions.active.description"
                      hide={this.hideOccasion}
                      show={this.showOccasion}
                      edit={this.editOccasion}
                    />
                  )
                },
                {
                  title: formatMessage({id: "admin.occasions.tabs.hidden"}, {count: hiddenOccasions.length}),
                  key: "hidden",
                  component: (
                    <OccasionsTab
                      items={hiddenOccasions}
                      description="admin.occasions.hidden.description"
                      hide={this.hideOccasion}
                      show={this.showOccasion}
                    />
                  )
                }
              ]}
            />

            <FullModal id={idNamespace} onHide={this.closeModal} show={showCreateModal}>
              <EditForm occasion={selectedOccasion} upsertOccasion={this.upsertOccasion} closeModal={this.closeModal} />
            </FullModal>
          </>
        )}
      </>
    )
  }
}

export default injectIntl(CompanyValuesList)
